<template>
  <last-data-preview
    v-if="isDataPreviewModalOpened"
    :data="dataForModal.data"
    :is-last-data-modal-opened="isDataPreviewModalOpened"
    :name="selectedMetadata[0]?.name"
    :preview="true"
    @toggle-modal="isDataPreviewModalOpened = false"
  />
  <create-form
    v-if="isCreateModalOpened"
    :is-create-modal-opened="isCreateModalOpened"
    :refresh-data="getDeviceMetadata"
    @toggle-modal="isCreateModalOpened = false"
  />
  <edit-form
    v-if="isEditModalOpened"
    :device-metadata="selectedMetadata[0]"
    :is-edit-modal-opened="isEditModalOpened"
    :refresh-data="getDeviceMetadata"
    @toggle-modal="isEditModalOpened = false"
  />
  <delete-modal
    v-if="isDeleteModalOpened"
    :is-deleting="isDeleteModalOpened"
    @cancel-delete="isDeleteModalOpened = false"
    @toggle-modal="isDeleteModalOpened = false"
    @confirm-delete="openConfirmationModal('delete')"
  />
  <confirmation-modal
    :is-confirmation-modal-opened="isConfirmationModalOpened"
    delete-text="delete"
    @confirm-action="deleteDeviceMetadata"
    @close-confirmation-modal="isConfirmationModalOpened = false"
    @toggle-modal="isConfirmationModalOpened = false"
  />
  <main-section>
    <loader v-if="isLoading" />
    <div v-else>
      <table-action-header
        :icon="['fas', 'add']"
        :permission-id="permissions.CreateDevice"
        :title="$t('DeviceMetadata.Title')"
        description=" "
        button-text="Create"
        @execute-method="toggleCreateModal"
      />
      <custom-table
        :options="options"
        :table-data="deviceMetadataPagination.items"
        :table-headers="tableHeaders"
        @force-refresh="getDeviceMetadata"
        @open-data-modal="displayData"
        @selected-rows="getSelectedRow"
      />
      <pagination
        :pagination-data="deviceMetadataPagination"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      />
    </div>
  </main-section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MainSection from "@/core/Components/MainSection.vue";
import { DeviceMetadataService } from "@/modules/Production/Submodules/DeviceMetadata/Services/DeviceMetadataService";
import {
  DeviceMetadata,
  DeviceMetadataObject,
} from "@/modules/Production/Submodules/DeviceMetadata/Services/DeviceMetadataTypes";
import Loader from "@/core/Components/Loader.vue";
import TableActionHeader from "@/core/Components/TableActionHeader.vue";
import CustomTable from "@/core/Components/CustomTable.vue";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import LastDataPreview from "@/modules/Devices/Submodules/Assignments/Components/LastDataPreview.vue";
import TheButton from "@/core/Components/TheButton.vue";
import CreateForm from "../Components/CreateForm.vue";
import ConfirmationModal from "@/core/Components/Notifications/ConfirmationModal.vue";
import DeleteModal from "@/core/Components/Notifications/DeleteModal.vue";
import EditForm from "../Components/EditForm.vue";
import Pagination from "@/core/Components/Pagination.vue";
import { PaginationSettings } from "@/core/Services/PaginationSettings";
import { Permissions } from "@/core/Services/Enums";
import { TableDropdownItem } from "@/core/Services/GlobalTypes";

export default defineComponent({
  components: {
    DeleteModal,
    ConfirmationModal,
    TheButton,
    LastDataPreview,
    CustomTable,
    TableActionHeader,
    Loader,
    MainSection,
    CreateForm,
    EditForm,
    Pagination,
  },

  data() {
    return {
      deviceMetadataService: new DeviceMetadataService(),
      deviceMetadata: [] as DeviceMetadata[],
      currentPage: PaginationSettings.DEFAULT_CURRENT_PAGE,
      currentPageSize: PaginationSettings.DEFAULT_CURRENT_PAGE_SIZE,
      deviceMetadataPagination: {} as DeviceMetadataObject,
      toast: useToast(),
      permissions: Permissions,
      dataForModal: {} as any,
      selectedMetadata: [] as DeviceMetadata[],
      isDataPreviewModalOpened: false,
      isLoading: false,
      isDataSourceLoading: false,
      isCreateModalOpened: false,
      isEditModalOpened: false,
      isDeleteModalOpened: false,
      isConfirmationModalOpened: false,
      tableHeaders: {
        id: { header: "DeviceTypeMetadata.ID", visible: false },
        name: { header: "DeviceTypeMetadata.Name", visible: true },
        "metadataType.name": { header: "DeviceTypeMetadata.MetadataTypes", visible: true },
        data: { header: "DeviceTypeMetadata.Data", visible: true },
      },
      options: {
        edit: {
          header: "Action.Edit",
          icon: ["fas", "pen"],
          permissionId: Permissions.EditDevice,
          action: this.toggleEditModal,
        },
        delete: {
          header: "Action.Delete",
          icon: ["fas", "trash"],
          permissionId: Permissions.DeleteDevice,
          action: this.toggleDeleteModal,
        },
      } as TableDropdownItem,
    };
  },
  async created() {
    this.isLoading = true;
    try {
      await this.getDeviceMetadata();
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    handlePageChange(newPage: number) {
      this.currentPage = newPage;
      this.getDeviceMetadata();
    },
    handlePageSizeChange(newPageSize: number) {
      this.currentPageSize = newPageSize;
      this.currentPage = 1;
      this.getDeviceMetadata();
    },
    async getDeviceMetadata() {
      this.isDataSourceLoading = true;
      try {
        this.deviceMetadataPagination =
          await this.deviceMetadataService.getDeviceMetadata(
            this.currentPage,
            this.currentPageSize
          );
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isDataSourceLoading = false;
      }
    },
    async deleteDeviceMetadata() {
      try {
        await this.deviceMetadataService.deleteDeviceMetadata(
          this.selectedMetadata[0].id
        );
        this.toast.success((this as any).$t("General.DeletedSuccessfully"));
        this.isConfirmationModalOpened = false;
        this.isDeleteModalOpened = false;
        await this.getDeviceMetadata();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      }
    },
    getSelectedRow(selectedRow: DeviceMetadata[]) {
      this.selectedMetadata = selectedRow;
    },
    displayData(data: any) {
      this.isDataPreviewModalOpened = true;
      this.dataForModal = data;
    },
    toggleCreateModal() {
      this.isCreateModalOpened = !this.isCreateModalOpened;
    },
    toggleEditModal() {
      this.isEditModalOpened = !this.isEditModalOpened;
    },
    toggleDeleteModal() {
      this.isDeleteModalOpened = !this.isDeleteModalOpened;
    },
    openConfirmationModal(key: string) {
      this.isConfirmationModalOpened = true;
    },
  },
});
</script>

<style scoped></style>
